import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

import { Sidebar } from "@/components/layout/Sidebar";
import { Header } from "@/components/layout/Header";
import { Dashboard } from "@/pages/Dashboard";
import { TeamsPage } from "@/pages/TeamsPage";
import { LeaguesPage } from "@/pages/LeaguesPage";
import { MatchesPage } from "@/pages/MatchesPage";
import { GeneratorPage } from "@/pages/GeneratorPage";
import { BrandsPage } from "@/pages/BrandsPage";
import { MediaPage } from "@/pages/MediaPage";
import { LoginPage } from "@/pages/LoginPage";
import { getToken, verify, logout as apiLogout } from "@/lib/api";
import { useStore } from "@/store/useStore";

function AppLayout({ children, onLogout }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="flex h-screen bg-background">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="flex-1 flex flex-col overflow-hidden lg:ml-64">
        <Header onMenuClick={() => setSidebarOpen(true)} onLogout={onLogout} />
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">{children}</main>
      </div>
      <Toaster position="top-right" richColors />
    </div>
  );
}

function AppShell({ onLogout }) {
  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/"
          element={<AppLayout onLogout={onLogout}><Dashboard /></AppLayout>}
        />
        <Route
          path="/teams"
          element={<AppLayout onLogout={onLogout}><TeamsPage /></AppLayout>}
        />
        <Route
          path="/leagues"
          element={<AppLayout onLogout={onLogout}><LeaguesPage /></AppLayout>}
        />
        <Route
          path="/matches"
          element={<AppLayout onLogout={onLogout}><MatchesPage /></AppLayout>}
        />
        <Route
          path="/generator"
          element={<AppLayout onLogout={onLogout}><GeneratorPage /></AppLayout>}
        />
        <Route
          path="/brands"
          element={<AppLayout onLogout={onLogout}><BrandsPage /></AppLayout>}
        />
        <Route
          path="/media"
          element={<AppLayout onLogout={onLogout}><MediaPage /></AppLayout>}
        />
      </Routes>
    </BrowserRouter>
  );
}

function LoadingScreen({ label = "Loading..." }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-3" data-testid="loading-screen">
        <div className="w-10 h-10 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
        <p className="text-sm text-muted-foreground">{label}</p>
      </div>
    </div>
  );
}

function App() {
  const [authState, setAuthState] = useState("checking"); // checking | unauth | ready
  const [bootLabel, setBootLabel] = useState("Verifying session...");
  const { hydrate, migrateLegacyIfNeeded, reset } = useStore();

  const boot = useCallback(async () => {
    setBootLabel("Verifying session...");
    const ok = await verify();
    if (!ok) {
      setAuthState("unauth");
      return;
    }
    try {
      setBootLabel("Checking for local data to migrate...");
      const result = await migrateLegacyIfNeeded();
      if (result?.migrated) {
        const c = result.counts;
        toast.success("Local data migrated to cloud", {
          description: `${c.teams} teams, ${c.leagues} leagues, ${c.matches} matches`,
        });
      }
      setBootLabel("Loading data...");
      await hydrate();
      setAuthState("ready");
    } catch (err) {
      if (err?.response?.status === 401) {
        apiLogout();
        setAuthState("unauth");
      } else {
        toast.error("Failed to load data. Retrying...");
        setAuthState("unauth");
      }
    }
  }, [hydrate, migrateLegacyIfNeeded]);

  useEffect(() => {
    if (!getToken()) {
      setAuthState("unauth");
      return;
    }
    boot();
  }, [boot]);

  const handleLoggedIn = () => {
    boot();
  };

  const handleLogout = () => {
    apiLogout();
    reset();
    setAuthState("unauth");
  };

  if (authState === "checking") return <LoadingScreen label={bootLabel} />;
  if (authState === "unauth") {
    return (
      <>
        <LoginPage onLoggedIn={handleLoggedIn} />
        <Toaster position="top-right" richColors />
      </>
    );
  }
  return <AppShell onLogout={handleLogout} />;
}

export default App;
